import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import {
  Box,
  Heading,
  Text,
  Link,
  VStack,
  Spinner,
  Center,
  Container,
  Button,
  Flex,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';

const BlogPage = () => {
  const [blogData, setBlogData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        // Fetch from the first WordPress API
        const res1 = await fetch('https://public-api.wordpress.com/wp/v2/sites/unlimiteye.wordpress.com/posts');
        const data1 = await res1.json();

        // Fetch from the second WordPress API
        const res2 = await fetch('https://public-api.wordpress.com/wp/v2/sites/guptamanjari.wordpress.com/posts');
        const data2 = await res2.json();

        // Combine both data arrays
        const combinedData = [...data1, ...data2];

        // Sort combined data by date (descending)
        const sortedData = combinedData.sort((a, b) => new Date(b.date) - new Date(a.date));

        setBlogData(sortedData);
        setLoading(false);
      } catch (error) {
        setError('Failed to load blog data');
        setLoading(false);
      }
    };

    fetchBlogData();
  }, []);

  const cleanText = (text) => {
    const doc = new DOMParser().parseFromString(text, 'text/html');
    return doc.body.textContent.replace(/&nbsp;/g, ' ').replace(/&#8217;/g, "'").replace(/&hellip;/g, '...');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const truncateText = (text, maxLength = 200) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const hoverBorderColor = useColorModeValue('blue.300', 'blue.500');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const titleColor = useColorModeValue('gray.800', 'white');
  const bgGradient = useColorModeValue('linear(to-b, gray.50, white)', 'linear(to-b, gray.900, gray.800)');

  if (loading) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center h="100vh">
        <Text fontSize="xl" color="red.500">
          {error}
        </Text>
      </Center>
    );
  }

  return (
    <>
      {/* Add Head for SEO optimization */}
      <Head>
        <title>Blog - AI in Recruitment & Talent Acquisition | Aptofy</title>
        <meta name="description" content="Latest articles on AI in recruitment, talent acquisition, and using AI bots for hiring." />
        <meta name="keywords" content="AI bots, talent acquisition, recruitment, Aptofy, AI recruitment, hiring bots, recruitment automation" />
        <meta property="og:title" content="Blog - AI in Recruitment & Talent Acquisition" />
        <meta property="og:description" content="Explore insights on AI-driven recruitment, hiring bots, and talent acquisition strategies." />
        <meta property="og:type" content="website" />
      </Head>
      <Box bgGradient={bgGradient} minH="100vh" py={12}>
        <Container maxW="container.lg" px={{ base: 4, md: 8 }}>
          {/* Page Header */}
          <Box textAlign="center" mb={12}>
            <Heading 
              as="h1" 
              size="2xl" 
              mb={4}
              bgGradient="linear(to-r, blue.600, purple.600)"
              bgClip="text"
              fontWeight="bold"
            >
              Our Blog
            </Heading>
            <Text fontSize="lg" color={textColor} maxW="2xl" mx="auto">
              Explore insights on AI-driven recruitment, talent acquisition, and hiring strategies
            </Text>
          </Box>

          <VStack spacing={6} align="stretch">
            {blogData.map((blog, index) => (
              <Box
                key={blog.id}
                as="article"
                bg={cardBg}
                borderWidth="1px"
                borderColor={borderColor}
                borderRadius="xl"
                overflow="hidden"
                boxShadow="lg"
                p={{ base: 6, md: 8 }}
                transition="all 0.3s ease"
                _hover={{
                  transform: 'translateY(-4px)',
                  boxShadow: 'xl',
                  borderColor: hoverBorderColor,
                }}
                position="relative"
              >
                {/* Decorative accent */}
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  w="4px"
                  h="100%"
                  bgGradient="linear(to-b, blue.500, purple.500)"
                />

                <Flex direction="column" pl={6}>
                  {/* Title */}
                  <Heading 
                    as="h2" 
                    size="xl" 
                    mb={3}
                    color={titleColor}
                    fontWeight="700"
                    lineHeight="1.3"
                    _hover={{
                      color: 'blue.600',
                    }}
                    transition="color 0.2s"
                  >
                    {cleanText(blog.title.rendered)}
                  </Heading>

                  {/* Date Badge */}
                  {blog.date && (
                    <Flex mb={4} gap={2} flexWrap="wrap">
                      <Badge 
                        colorScheme="blue" 
                        variant="subtle"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm"
                      >
                        {formatDate(blog.date)}
                      </Badge>
                    </Flex>
                  )}

                  {/* Excerpt */}
                  <Text 
                    as="p" 
                    fontSize="md" 
                    mb={6}
                    color={textColor}
                    lineHeight="1.7"
                  >
                    {truncateText(cleanText(blog.excerpt.rendered.replace(/<[^>]*>/g, '')))}
                  </Text>

                  {/* Read More Button */}
                  <Flex justify="flex-start">
                    <Button
                      as={Link}
                      href={blog.link}
                      isExternal
                      colorScheme="blue"
                      variant="solid"
                      size="md"
                      borderRadius="md"
                      fontWeight="600"
                      px={6}
                      _hover={{
                        transform: 'translateX(4px)',
                        boxShadow: 'md',
                      }}
                      transition="all 0.2s"
                    >
                      Read More →
                    </Button>
                  </Flex>
                </Flex>
              </Box>
            ))}
          </VStack>

          {/* Empty state message */}
          {blogData.length === 0 && !loading && (
            <Center py={20}>
              <Text fontSize="lg" color={textColor}>
                No blog posts available at the moment.
              </Text>
            </Center>
          )}
        </Container>
      </Box>
    </>
  );
};

export default BlogPage;

